import React, { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, X, Download, FileImage, FileText as FilePdf, PackageSearch, Edit2 } from 'lucide-react'
import { getBills, createBill, updateBill, getParts } from '../../services/staffServices'
import { useStaffAuthStore } from '../../store/staffAuthStore'
import StaffLayout from './components/StaffLayout'
import toast from 'react-hot-toast'
import { ModalOverlay } from './components/StaffAnimations'

const STATUS_COLORS = {
    Draft: 'bg-slate-500/15 text-slate-400',
    Sent: 'bg-blue-500/15 text-blue-400',
    Paid: 'bg-green-500/15 text-green-400',
    Cancelled: 'bg-red-500/15 text-red-400',
    Overdue: 'bg-orange-500/15 text-orange-400',
}

const normalizeItem = (it) => ({
    description: it.description || it.name || '',
    notes: it.notes || '',
    quantity: it.quantity ?? it.qty ?? 1,
    unit_price: it.unit_price ?? it.rate ?? 0,
})

// ── Bill Preview (printable) ──────────────────────────────────────────────────
function BillPreview({ bill, forwardRef }) {
    const rawItems = bill?.items
    let parsed = []
    if (Array.isArray(rawItems)) parsed = rawItems
    else if (typeof rawItems === 'string') { try { parsed = JSON.parse(rawItems) } catch {} }
    else if (rawItems && typeof rawItems === 'object') parsed = [rawItems]
    const items = parsed.map(normalizeItem)
    const subtotal = parseFloat(bill?.subtotal || 0)
    const discount = parseFloat(bill?.discount || 0)
    const total = parseFloat(bill?.total || 0)

    return (
        <div
            ref={forwardRef}
            style={{
                background: '#ffffff', color: '#111827', fontFamily: 'Inter, Arial, sans-serif',
                width: '794px', minHeight: '1123px', padding: '48px', boxSizing: 'border-box',
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <img src="/new_logo.png" alt="Logo" style={{ width: '48px', height: '48px', objectFit: 'contain' }} onError={e => { e.target.style.display = 'none' }} />
                    <div>
                        <p style={{ fontSize: '20px', fontWeight: '700', margin: 0, color: '#111827' }}>Abha Enterprises</p>
                        <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>Premium EV Dealership — Kolkata</p>
                    </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                    <p style={{ fontSize: '24px', fontWeight: '700', color: '#1d4ed8', margin: 0 }}>BILL</p>
                    <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>{bill?.bill_number}</p>
                    {bill?.valid_until && (
                        <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                            Valid until: {new Date(bill.valid_until + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </p>
                    )}
                </div>
            </div>

            <div style={{ height: '2px', background: '#1d4ed8', marginBottom: '28px', borderRadius: '2px' }} />

            <div style={{ marginBottom: '28px' }}>
                <p style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>Bill To</p>
                <p style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: 0 }}>{bill?.customer_name || '—'}</p>
                {bill?.customer_phone && <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>{bill.customer_phone}</p>}
                {bill?.customer_address && <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>{bill.customer_address}</p>}
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '24px' }}>
                <thead>
                    <tr style={{ background: '#1d4ed8' }}>
                        <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: '12px', fontWeight: '600', color: '#fff', borderRadius: '4px 0 0 0' }}>#</th>
                        <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: '12px', fontWeight: '600', color: '#fff' }}>Description</th>
                        <th style={{ textAlign: 'center', padding: '10px 12px', fontSize: '12px', fontWeight: '600', color: '#fff' }}>Qty</th>
                        <th style={{ textAlign: 'right', padding: '10px 12px', fontSize: '12px', fontWeight: '600', color: '#fff' }}>Rate (₹)</th>
                        <th style={{ textAlign: 'right', padding: '10px 12px', fontSize: '12px', fontWeight: '600', color: '#fff', borderRadius: '0 4px 0 0' }}>Amount (₹)</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((item, i) => {
                        const amount = (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0)
                        return (
                            <tr key={i} style={{ background: i % 2 === 0 ? '#f9fafb' : '#ffffff' }}>
                                <td style={{ padding: '10px 12px', fontSize: '13px', color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>{i + 1}</td>
                                <td style={{ padding: '10px 12px', fontSize: '13px', color: '#111827', borderBottom: '1px solid #e5e7eb' }}>
                                    {item.description || '—'}
                                    {item.notes && <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>{item.notes}</div>}
                                </td>
                                <td style={{ padding: '10px 12px', fontSize: '13px', color: '#374151', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>{item.quantity}</td>
                                <td style={{ padding: '10px 12px', fontSize: '13px', color: '#374151', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {parseFloat(item.unit_price || 0).toLocaleString('en-IN')}
                                </td>
                                <td style={{ padding: '10px 12px', fontSize: '13px', fontWeight: '500', color: '#111827', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {amount.toLocaleString('en-IN')}
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '28px' }}>
                <div style={{ width: '280px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '13px', color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>
                        <span>Subtotal</span><span>₹{subtotal.toLocaleString('en-IN')}</span>
                    </div>
                    {discount > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '13px', color: '#dc2626', borderBottom: '1px solid #e5e7eb' }}>
                            <span>Discount</span><span>— ₹{discount.toLocaleString('en-IN')}</span>
                        </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 12px', fontSize: '16px', fontWeight: '700', color: '#ffffff', background: '#1d4ed8', borderRadius: '8px', marginTop: '6px' }}>
                        <span>Total</span><span>₹{total.toLocaleString('en-IN')}</span>
                    </div>
                </div>
            </div>

            {bill?.notes && (
                <div style={{ background: '#f3f4f6', borderRadius: '8px', padding: '14px', marginBottom: '24px' }}>
                    <p style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', marginBottom: '4px' }}>Notes</p>
                    <p style={{ fontSize: '13px', color: '#374151', margin: 0 }}>{bill.notes}</p>
                </div>
            )}

            <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>Created by: {bill?.created_by || '—'}</p>
                    <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>abhaev.store</p>
                </div>
                <div style={{ textAlign: 'right' }}>
                    <p style={{ fontSize: '11px', color: '#9ca3af', margin: 0 }}>This is a computer-generated bill</p>
                </div>
            </div>
        </div>
    )
}

// ── Export Modal ──────────────────────────────────────────────────────────────
function ExportModal({ bill, onClose }) {
    const previewRef = useRef(null)
    const [exporting, setExporting] = useState(null)

    const exportPNG = async () => {
        setExporting('png')
        try {
            const { default: html2canvas } = await import('html2canvas')
            const canvas = await html2canvas(previewRef.current, { scale: 2, useCORS: true, backgroundColor: '#ffffff', logging: false })
            const link = document.createElement('a')
            link.download = `${bill.bill_number || 'bill'}.png`
            link.href = canvas.toDataURL('image/png')
            link.click()
            toast.success('PNG exported')
        } catch (e) { toast.error('PNG export failed: ' + e.message) } finally { setExporting(null) }
    }

    const exportPDF = async () => {
        setExporting('pdf')
        try {
            const { default: html2canvas } = await import('html2canvas')
            const { default: jsPDF } = await import('jspdf')
            const canvas = await html2canvas(previewRef.current, { scale: 2, useCORS: true, backgroundColor: '#ffffff', logging: false })
            const imgData = canvas.toDataURL('image/png')
            const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
            const pdfW = pdf.internal.pageSize.getWidth()
            const pdfH = (canvas.height * pdfW) / canvas.width
            pdf.addImage(imgData, 'PNG', 0, 0, pdfW, pdfH)
            pdf.save(`${bill.bill_number || 'bill'}.pdf`)
            toast.success('PDF exported')
        } catch (e) { toast.error('PDF export failed: ' + e.message) } finally { setExporting(null) }
    }

    return (
        <div className="fixed inset-0 bg-black/80 z-50 flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 bg-slate-900 border-b border-white/8 shrink-0">
                <span className="text-sm font-medium text-white">{bill.bill_number}</span>
                <div className="flex items-center gap-2">
                    <button onClick={exportPNG} disabled={!!exporting}
                        className="flex items-center gap-1.5 bg-green-600 hover:bg-green-500 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition">
                        <FileImage size={13} /> {exporting === 'png' ? 'Exporting...' : 'Export PNG'}
                    </button>
                    <button onClick={exportPDF} disabled={!!exporting}
                        className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition">
                        <FilePdf size={13} /> {exporting === 'pdf' ? 'Exporting...' : 'Export PDF'}
                    </button>
                    <button onClick={onClose} className="text-slate-400 hover:text-white p-1.5 rounded-lg hover:bg-white/5 transition"><X size={16} /></button>
                </div>
            </div>
            <div className="flex-1 overflow-auto flex justify-center py-6 px-4">
                <div style={{ transform: 'scale(0.85)', transformOrigin: 'top center' }}>
                    <BillPreview bill={bill} forwardRef={previewRef} />
                </div>
            </div>
        </div>
    )
}

// ── Create Modal ──────────────────────────────────────────────────────────────
function BillModal({ bill, profile, onClose, onSave }) {
    const isEdit = !!bill?.id
    const [form, setForm] = useState({
        bill_number: bill?.bill_number || `BILL-${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }).replace(/ /g, '').toUpperCase()}-${Math.floor(Math.random() * 9000 + 1000)}`,
        customer_name: bill?.customer_name || '',
        customer_phone: bill?.customer_phone || '',
        customer_address: bill?.customer_address || '',
        notes: bill?.notes || '',
        status: bill?.status || 'Draft',
        valid_until: bill?.valid_until || '',
        discount: bill?.discount || 0,
    })
    const [items, setItems] = useState(
        bill?.items && Array.isArray(bill.items)
            ? bill.items.map(normalizeItem)
            : [{ description: '', notes: '', quantity: 1, unit_price: 0 }]
    )
    const [priceTier, setPriceTier] = useState('dealer')
    const [pickPart, setPickPart] = useState('')
    const { data: parts = [] } = useQuery({ queryKey: ['parts'], queryFn: () => getParts('') })
    const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

    const setItem = (i, k, v) => {
        setItems(prev => {
            const next = [...prev]
            next[i] = { ...next[i], [k]: v }
            return next
        })
    }
    const addRow = () => setItems(p => [...p, { description: '', notes: '', quantity: 1, unit_price: 0 }])
    const removeRow = (i) => setItems(p => p.filter((_, j) => j !== i))

    const addFromPart = () => {
        const part = parts.find(p => p.id === pickPart)
        if (!part) { toast.error('Select a part first'); return }
        const rate = priceTier === 'dealer' ? (parseFloat(part.dealer_price) || 0) : (parseFloat(part.sell_price) || 0)
        setItems(p => [...p, { description: part.name, notes: part.sku ? `SKU: ${part.sku}` : '', quantity: 1, unit_price: rate }])
        setPickPart('')
    }

    const subtotal = items.reduce((s, i) => s + ((parseFloat(i.quantity) || 0) * (parseFloat(i.unit_price) || 0)), 0)
    const total = subtotal - (parseFloat(form.discount) || 0)

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className="bg-slate-900 rounded-2xl border border-white/8 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-5 border-b border-white/5 sticky top-0 bg-slate-900 z-10">
                    <h2 className="font-semibold text-white">{isEdit ? 'Edit Bill' : 'New Bill'}</h2>
                    <button onClick={onClose} className="text-slate-400 hover:text-white"><X size={18} /></button>
                </div>
                <form onSubmit={e => {
                    e.preventDefault()
                    if (!form.customer_name) { toast.error('Customer name required'); return }
                    if (items.length === 0 || !items[0].description) { toast.error('Add at least one item'); return }
                    onSave({
                        ...form,
                        items,
                        subtotal,
                        total,
                        discount: parseFloat(form.discount) || 0,
                        created_by: profile?.name || '',
                        created_by_id: profile?.id || '',
                    })
                }} className="p-5 space-y-4">

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Bill No.</label>
                            <input value={form.bill_number} onChange={e => set('bill_number', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500" />
                        </div>
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Valid Until</label>
                            <input type="date" value={form.valid_until} onChange={e => set('valid_until', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500" />
                        </div>
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Customer Name *</label>
                            <input value={form.customer_name} onChange={e => set('customer_name', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500" />
                        </div>
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Phone</label>
                            <input value={form.customer_phone} onChange={e => set('customer_phone', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500" />
                        </div>
                        <div className="col-span-2">
                            <label className="text-xs text-slate-400 block mb-1">Address</label>
                            <input value={form.customer_address} onChange={e => set('customer_address', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500" />
                        </div>
                    </div>

                    {/* Parts picker helper */}
                    <div className="bg-slate-800/60 rounded-xl p-3">
                        <div className="flex items-center gap-2 mb-2">
                            <PackageSearch size={14} className="text-blue-400" />
                            <span className="text-xs font-medium text-slate-300">Add from Parts Inventory</span>
                        </div>
                        <div className="flex gap-2">
                            <select value={pickPart} onChange={e => setPickPart(e.target.value)}
                                className="flex-1 bg-slate-800 border border-white/8 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500">
                                <option value="">Select a part...</option>
                                {parts.map(p => (
                                    <option key={p.id} value={p.id}>
                                        {p.name} — Dealer: ₹{parseFloat(p.dealer_price || 0).toLocaleString('en-IN')} · Customer: ₹{parseFloat(p.sell_price || 0).toLocaleString('en-IN')}
                                    </option>
                                ))}
                            </select>
                            <select value={priceTier} onChange={e => setPriceTier(e.target.value)}
                                className="bg-slate-800 border border-white/8 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500">
                                <option value="dealer">Dealer Price</option>
                                <option value="customer">Customer Price</option>
                            </select>
                            <button type="button" onClick={addFromPart}
                                className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg text-sm font-medium transition">Add</button>
                        </div>
                    </div>

                    {/* Items */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="text-xs text-slate-400">Items</label>
                            <button type="button" onClick={addRow} className="text-xs text-blue-400 hover:text-blue-300">+ Add row</button>
                        </div>
                        <div className="space-y-2">
                            <div className="grid grid-cols-12 gap-2 text-xs text-slate-500 px-1">
                                <span className="col-span-5">Description</span>
                                <span className="col-span-2">Qty</span>
                                <span className="col-span-2">Rate (₹)</span>
                                <span className="col-span-2">Amount</span>
                                <span className="col-span-1"></span>
                            </div>
                            {items.map((item, i) => {
                                const amount = (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0)
                                return (
                                    <div key={i} className="grid grid-cols-12 gap-2">
                                        <input value={item.description} onChange={e => setItem(i, 'description', e.target.value)}
                                            placeholder="Item name"
                                            className="col-span-5 bg-slate-800 border border-white/8 rounded-lg px-2 py-2 text-sm text-white focus:outline-none focus:border-blue-500" />
                                        <input type="number" min="0" value={item.quantity} onChange={e => setItem(i, 'quantity', e.target.value)}
                                            className="col-span-2 bg-slate-800 border border-white/8 rounded-lg px-2 py-2 text-sm text-white focus:outline-none focus:border-blue-500" />
                                        <input type="number" min="0" value={item.unit_price} onChange={e => setItem(i, 'unit_price', e.target.value)}
                                            className="col-span-2 bg-slate-800 border border-white/8 rounded-lg px-2 py-2 text-sm text-white focus:outline-none focus:border-blue-500" />
                                        <div className="col-span-2 flex items-center px-2 text-sm text-slate-300">
                                            ₹{amount.toLocaleString('en-IN')}
                                        </div>
                                        <button type="button" onClick={() => removeRow(i)} className="col-span-1 text-slate-500 hover:text-red-400 flex items-center justify-center">
                                            <X size={14} />
                                        </button>
                                    </div>
                                )
                            })}
                        </div>
                    </div>

                    {/* Totals */}
                    <div className="bg-slate-800 rounded-xl p-4 space-y-2 text-sm">
                        <div className="flex justify-between text-slate-400">
                            <span>Subtotal</span><span>₹{subtotal.toLocaleString('en-IN')}</span>
                        </div>
                        <div className="flex justify-between items-center text-slate-400">
                            <span>Discount</span>
                            <input type="number" min="0" value={form.discount} onChange={e => set('discount', e.target.value)}
                                className="w-24 bg-slate-700 border border-white/8 rounded-lg px-2 py-1 text-sm text-white text-right focus:outline-none" />
                        </div>
                        <div className="flex justify-between font-semibold text-white border-t border-white/5 pt-2">
                            <span>Total</span><span>₹{total.toLocaleString('en-IN')}</span>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Status</label>
                            <select value={form.status} onChange={e => set('status', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500">
                                <option>Draft</option><option>Sent</option><option>Paid</option><option>Cancelled</option><option>Overdue</option>
                            </select>
                        </div>
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Notes</label>
                            <input value={form.notes} onChange={e => set('notes', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500" />
                        </div>
                    </div>

                    <div className="flex gap-3 pt-2">
                        <button type="button" onClick={onClose} className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-300 py-2.5 rounded-xl text-sm transition">Cancel</button>
                        <button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-500 text-white py-2.5 rounded-xl text-sm font-medium transition">{isEdit ? 'Update' : 'Create Bill'}</button>
                    </div>
                </form>
            </div>
        </div>
    )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Billing() {
    const qc = useQueryClient()
    const { profile } = useStaffAuthStore()
    const [modal, setModal] = useState(null)
    const [exportBill, setExportBill] = useState(null)

    const { data: bills = [], isLoading } = useQuery({ queryKey: ['bills'], queryFn: getBills })

    const createMut = useMutation({
        mutationFn: createBill,
        onSuccess: () => { qc.invalidateQueries(['bills']); setModal(null); toast.success('Bill created') },
        onError: e => toast.error(e.message),
    })
    const updateMut = useMutation({
        mutationFn: ({ id, ...r }) => updateBill(id, r),
        onSuccess: () => { qc.invalidateQueries(['bills']); setModal(null); toast.success('Updated') },
        onError: e => toast.error(e.message),
    })

    return (
        <StaffLayout>
            <div className="space-y-5">
                <div className="flex items-center justify-between">
                    <h1 className="text-xl font-bold text-white">Billing</h1>
                    <button onClick={() => setModal('new')} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-xl text-sm font-medium transition">
                        <Plus size={16} /> New Bill
                    </button>
                </div>

                {/* Status summary */}
                <div className="grid grid-cols-5 gap-3">
                    {['Draft', 'Sent', 'Paid', 'Cancelled', 'Overdue'].map(s => (
                        <div key={s} className="bg-slate-900 rounded-xl border border-white/5 p-4">
                            <p className="text-xs text-slate-400">{s}</p>
                            <p className="text-xl font-bold text-white mt-1">{bills.filter(b => b.status === s).length}</p>
                        </div>
                    ))}
                </div>

                <div className="bg-slate-900 rounded-xl border border-white/5 overflow-hidden">
                    {isLoading ? (
                        <div className="p-8 text-center text-slate-400">Loading...</div>
                    ) : bills.length === 0 ? (
                        <div className="p-12 text-center text-slate-500">No bills yet</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-white/5">
                                        {['Bill No.', 'Customer', 'Phone', 'Total', 'Status', 'Valid Until', 'Created By', ''].map(h => (
                                            <th key={h} className="text-left text-xs font-medium text-slate-400 px-4 py-3">{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-white/5">
                                    {bills.map(b => (
                                        <tr key={b.id} className="hover:bg-white/2 transition group">
                                            <td className="px-4 py-3 text-slate-400 text-xs">{b.bill_number}</td>
                                            <td className="px-4 py-3 font-medium text-white">{b.customer_name}</td>
                                            <td className="px-4 py-3 text-slate-400">{b.customer_phone}</td>
                                            <td className="px-4 py-3 font-medium text-white">₹{parseFloat(b.total || 0).toLocaleString('en-IN')}</td>
                                            <td className="px-4 py-3">
                                                <span className={`text-xs px-2 py-1 rounded-lg font-medium ${STATUS_COLORS[b.status] || 'bg-slate-500/15 text-slate-400'}`}>
                                                    {b.status}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-slate-400">{b.valid_until || '—'}</td>
                                            <td className="px-4 py-3 text-slate-500 text-xs">{b.created_by}</td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition">
                                                    <button onClick={() => setExportBill(b)} className="text-slate-400 hover:text-green-400 transition p-1" title="Export">
                                                        <Download size={13} />
                                                    </button>
                                                    <button onClick={() => setModal(b)} className="text-slate-400 hover:text-blue-400 transition p-1" title="Edit">
                                                        <Edit2 size={13} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            <ModalOverlay show={!!modal}>
                {modal && (
                    <BillModal
                        bill={modal === 'new' ? null : modal}
                        profile={profile}
                        onClose={() => setModal(null)}
                        onSave={data => modal === 'new' ? createMut.mutate(data) : updateMut.mutate({ id: modal.id, ...data })}
                    />
                )}
            </ModalOverlay>

            <ModalOverlay show={!!exportBill}>
                {exportBill && <ExportModal bill={exportBill} onClose={() => setExportBill(null)} />}
            </ModalOverlay>
        </StaffLayout>
    )
}